import { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';

interface OptionValue {
  id: string;
  name: string;
  displayName: string;
  metadata: Record<string, unknown>;
  swatchUrl: string | null;
  mockupImageUrl: string | null;
  surcharge: string | null;
  sortOrder?: number;
}

interface OptionSelectorProps {
  type: string;
  uiType: string;
  values: OptionValue[];
  selectedId: string | null;
  onChange: (valueId: string) => void;
}

function isLightColor(hex: string): boolean {
  const c = hex.replace('#', '');
  if (c.length < 6) return true;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 186;
}

function getHexCode(metadata: Record<string, unknown>): string | null {
  if (typeof metadata.hexCode === 'string') return metadata.hexCode;
  if (typeof metadata.hex === 'string') return metadata.hex;
  return null;
}

function ColorSwatchSelector({
  values,
  selectedId,
  onChange,
}: Pick<OptionSelectorProps, 'values' | 'selectedId' | 'onChange'>) {
  return (
    <div className="flex flex-wrap gap-2">
      {values.map((val) => {
        const isSelected = val.id === selectedId;
        const hex = getHexCode(val.metadata) ?? '#e5e7eb';
        const light = isLightColor(hex);

        return (
          <button
            key={val.id}
            type="button"
            title={val.displayName || val.name}
            className={clsx(
              'relative h-10 w-10 rounded-full border-2 transition-all duration-150',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
              isSelected
                ? 'border-brand-600 ring-2 ring-brand-600/30 scale-110'
                : light
                  ? 'border-gray-300 hover:border-gray-400 hover:scale-105'
                  : 'border-transparent hover:border-gray-400 hover:scale-105',
            )}
            style={{ backgroundColor: hex }}
            onClick={() => onChange(val.id)}
          >
            {isSelected && (
              <span className="absolute inset-0 flex items-center justify-center">
                <svg
                  className={clsx('h-4 w-4', light ? 'text-gray-800' : 'text-white')}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ButtonGroupSelector({
  values,
  selectedId,
  onChange,
}: Pick<OptionSelectorProps, 'values' | 'selectedId' | 'onChange'>) {
  return (
    <div className="flex flex-wrap gap-2">
      {values.map((val) => {
        const isSelected = val.id === selectedId;
        return (
          <button
            key={val.id}
            type="button"
            className={clsx(
              'relative min-w-12 rounded-lg border-2 px-4 py-2.5 text-sm font-semibold',
              'transition-all duration-150',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
              isSelected
                ? 'border-brand-600 bg-brand-600 text-white shadow-sm'
                : 'border-border bg-white text-text-primary hover:border-brand-300 hover:bg-brand-50',
            )}
            onClick={() => onChange(val.id)}
          >
            {val.displayName || val.name}
          </button>
        );
      })}
    </div>
  );
}

function DropdownSelector({
  type,
  values,
  selectedId,
  onChange,
}: Pick<OptionSelectorProps, 'type' | 'values' | 'selectedId' | 'onChange'>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const selectedVal = values.find((v) => v.id === selectedId);
  const showSearch = values.length > 6;

  const filtered = search
    ? values.filter((v) =>
        (v.displayName || v.name).toLowerCase().includes(search.toLowerCase()),
      )
    : values;

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        className={clsx(
          'flex w-full items-center justify-between rounded-xl border-2 px-4 py-3 text-sm transition-all duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
          open
            ? 'border-brand-500 bg-white shadow-sm'
            : selectedId
              ? 'border-brand-200 bg-brand-50/30 hover:border-brand-300'
              : 'border-border bg-white hover:border-gray-400',
        )}
        onClick={() => setOpen(!open)}
      >
        <span className={clsx(selectedId ? 'font-medium text-text-primary' : 'text-text-tertiary')}>
          {selectedVal
            ? (selectedVal.displayName || selectedVal.name) +
              (selectedVal.surcharge && parseFloat(selectedVal.surcharge) > 0
                ? ` (+$${selectedVal.surcharge})`
                : '')
            : `Select ${type}`}
        </span>
        <svg
          className={clsx(
            'h-4 w-4 text-text-tertiary transition-transform duration-200',
            open && 'rotate-180',
          )}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1.5 overflow-hidden rounded-xl border border-border bg-white shadow-lg animate-in fade-in slide-in-from-top-1 duration-150">
          {showSearch && (
            <div className="border-b border-border p-2.5">
              <div className="relative">
                <svg
                  className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                </svg>
                <input
                  ref={searchRef}
                  type="text"
                  className="w-full rounded-lg border border-border bg-surface-secondary py-2 pl-8 pr-3 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400/30"
                  placeholder={`Search ${type.toLowerCase()}...`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-center text-sm text-text-tertiary">No options found</p>
            ) : (
              filtered.map((val) => {
                const isSelected = val.id === selectedId;
                const label = val.displayName || val.name;
                const surchargeText =
                  val.surcharge && parseFloat(val.surcharge) > 0
                    ? `+$${val.surcharge}`
                    : null;

                return (
                  <button
                    key={val.id}
                    type="button"
                    className={clsx(
                      'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors',
                      isSelected
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-text-primary hover:bg-surface-secondary',
                    )}
                    onClick={() => {
                      onChange(val.id);
                      setOpen(false);
                      setSearch('');
                    }}
                  >
                    <span
                      className={clsx(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors',
                        isSelected
                          ? 'border-brand-600 bg-brand-600'
                          : 'border-gray-300 bg-white',
                      )}
                    >
                      {isSelected && (
                        <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                      )}
                    </span>
                    <span className={clsx('flex-1', isSelected && 'font-medium')}>{label}</span>
                    {surchargeText && (
                      <span className="text-xs text-text-tertiary">{surchargeText}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function cardImageUrl(val: OptionValue): string | null {
  if (val.swatchUrl) return val.swatchUrl;
  if (val.mockupImageUrl) return val.mockupImageUrl;
  const image = val.metadata.imageUrl ?? val.metadata.image;
  return typeof image === 'string' && image.trim() !== '' ? image : null;
}

function ImageCardSelector({
  values,
  selectedId,
  onChange,
}: Pick<OptionSelectorProps, 'values' | 'selectedId' | 'onChange'>) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {values.map((val) => {
        const isSelected = val.id === selectedId;
        const image = cardImageUrl(val);
        const label = val.displayName || val.name;
        return (
          <button
            key={val.id}
            type="button"
            aria-pressed={isSelected}
            className={clsx(
              'overflow-hidden rounded-xl border-2 text-left transition-all duration-150',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
              isSelected
                ? 'border-brand-600 ring-2 ring-brand-600/30'
                : 'border-border bg-white hover:border-brand-300',
            )}
            onClick={() => onChange(val.id)}
          >
            <span className="flex aspect-square items-center justify-center bg-surface-secondary">
              {image ? (
                <img src={image} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="px-2 text-center text-xs text-text-tertiary">{label}</span>
              )}
            </span>
            <span className={clsx('block px-2 py-2 text-sm text-text-primary', isSelected && 'font-semibold')}>
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function RadioSelector({
  values,
  selectedId,
  onChange,
}: Pick<OptionSelectorProps, 'values' | 'selectedId' | 'onChange'>) {
  return (
    <div className="space-y-2">
      {values.map((val) => {
        const isSelected = val.id === selectedId;
        return (
          <button
            key={val.id}
            type="button"
            className={clsx(
              'flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3 text-left',
              'transition-all duration-150',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
              isSelected
                ? 'border-brand-600 bg-brand-50'
                : 'border-border bg-white hover:border-brand-300',
            )}
            onClick={() => onChange(val.id)}
          >
            <span
              className={clsx(
                'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                isSelected ? 'border-brand-600' : 'border-gray-300',
              )}
            >
              {isSelected && <span className="h-2.5 w-2.5 rounded-full bg-brand-600" />}
            </span>
            <span className={clsx('flex-1 text-sm text-text-primary', isSelected && 'font-medium')}>
              {val.displayName || val.name}
            </span>
            {val.surcharge && parseFloat(val.surcharge) > 0 && (
              <span className="text-xs text-text-tertiary">+${val.surcharge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function sortOptionValues(values: OptionValue[]): OptionValue[] {
  return [...values].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export function OptionSelector({
  type,
  uiType,
  values,
  selectedId,
  onChange,
}: OptionSelectorProps) {
  const sorted = sortOptionValues(values);
  const selectedVal = sorted.find((v) => v.id === selectedId);

  return (
    <div>
      <div className="mb-3 flex items-baseline gap-2">
        <h3 className="text-[15px] font-semibold text-text-primary">{type}</h3>
        {selectedVal && (
          <span className="text-sm text-text-secondary">
            — {selectedVal.displayName || selectedVal.name}
          </span>
        )}
      </div>

      {uiType === 'COLOR_SWATCH' && (
        <ColorSwatchSelector values={sorted} selectedId={selectedId} onChange={onChange} />
      )}
      {uiType === 'BUTTON_GROUP' && (
        <ButtonGroupSelector values={sorted} selectedId={selectedId} onChange={onChange} />
      )}
      {uiType === 'DROPDOWN' && (
        <DropdownSelector type={type} values={sorted} selectedId={selectedId} onChange={onChange} />
      )}
      {uiType === 'RADIO' && (
        <RadioSelector values={sorted} selectedId={selectedId} onChange={onChange} />
      )}
      {uiType === 'IMAGE_CARD' && (
        <ImageCardSelector values={sorted} selectedId={selectedId} onChange={onChange} />
      )}
      {!['COLOR_SWATCH', 'BUTTON_GROUP', 'DROPDOWN', 'RADIO', 'IMAGE_CARD'].includes(uiType) && (
        <DropdownSelector type={type} values={sorted} selectedId={selectedId} onChange={onChange} />
      )}
    </div>
  );
}
